import * as React from 'react';
import { useState } from 'react';

import { Location } from '~common/react/Location';
import { SiteSettingServerAdminTelemetryEvent } from '~common/telemetryEvents/TelemetryEvents';
import { buildSiteSettingServerAdminTelemetryEvent, getSiteSettingUUID } from '~common/telemetryEvents/util/SiteSettingTelemetryUtil';
import { Telemetry } from '~commonInfra/services/Telemetry';
import { NavLocationSelectors } from '~selectors/NavigationStateSelectors';
import { SessionServerSelectors } from '~selectors/SessionInfoSelectors';

import { GeneralSiteSettingsSubspaceState } from '../../../../app/spaces/SiteSettingsSpace/GeneralSiteSettingsSubspace';
import { SpacesSiteUrls } from '../../../../app/spaces/SpacesSiteUrls';
import { IResult } from '../../../../httpClient/serverApi/types/IResponse';
import { Sites } from '../../../../ts/siteManagement/services/resources/Sites';
import { SettingsFormikContainer } from '../../../components/common/SettingsFormikContainer';
import { IServerAdminSettings, ISiteSettingsForServerAdmin } from '../../../types/ISite';
import { useSelector } from '../../utils';

export type SiteInfo = {
  siteInfo: Pick<ISiteSettingsForServerAdmin,
    'id'
    | 'licensedUserCount'
    | 'creatorCount'
    | 'explorerCount'
    | 'viewerCount'>;
};

// TODO: Not implemented yet since SiteCreation is still in Angular
// Need to connect this subForm to the CreateSiteAction
export interface PropOverridesForSiteCreation {
  hideNameAndId?: boolean;
  hideAvailability?: boolean;
  hideRevisionHistoryLimitWarning?: boolean;
  isDefaultSite?: boolean;
}

/**
 * The settings passed here are really the top-level settings, but because we're
 * recursively passing the prop down, we have to keep it named "settings"
 */
export type ReactSiteSettingsForServerAdminSubFormikProps = PropOverridesForSiteCreation & {
  settings: Omit<GeneralSiteSettingsSubspaceState, 'siteAuthenticationSettings'>;
};

/**
 * A partial list of site settings for server admin.
 * This is meant for individual section components to pick a subset of the overall settings they use
 */
export type SiteSettingsForServerAdminProps<T extends keyof IServerAdminSettings = keyof IServerAdminSettings>
  = PropOverridesForSiteCreation & SiteInfo
  & {
    settings: Pick<IServerAdminSettings, T>;
    availability: string;
  };

export const serverAdminSections = [
  'SiteNameAndIdSection',
  'SiteAvailabilitySection',
  'StorageSection',
  'RevisionHistorySection',
  'ManageUsersSection',
  'SiteGuestAccessSection',
  'WebAuthoringSection',
  'PrepConductorSection',
  'CatalogingSection',
  'EmailSettingsSection',
  'CacheWarmupSection',
  'PerformanceMetricsSection',
  'ManagedKeychainCleanupSection',
  'AutoSuspendExtractRefreshSection',
];

function initializeSettingsWithDefaultValues(settings: IServerAdminSettings, defaultSubscriptionEmail: string) {
  return {
    ...settings,
    customSubscriptionEmail: settings.customSubscriptionEmail ?? defaultSubscriptionEmail,
  };
}

function useMemoizedSettingsWithDefaults(settings: ReactSiteSettingsForServerAdminSubFormikProps['settings'], defaultSubscriptionEmail: string) {
  return React.useMemo(
    () =>
      initializeSettingsWithDefaultValues(
        settings.siteSettingsForServerAdmin.settings,
        defaultSubscriptionEmail
      ),
    // This deps array was noncompliant before we enabled the lint rule. If you're here, please consider fixing it! See TFS 1257824
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      settings.siteSettingsForServerAdmin.settings,
      settings.siteSettingsForServerAdmin.settings.customSubscriptionEmail,
      defaultSubscriptionEmail,
    ]
  );
}

/**
 * Responsible for setting up the individual settings inside it's subform
 * and for defining it's own handleSubmit (where updateSettings API call is made)
 */
const ReactSiteSettingsForServerAdmin: React.FunctionComponent<ReactSiteSettingsForServerAdminSubFormikProps> = props => {
  let {settings, ...rest} = props;
  const defaultSubscriptionEmail = useSelector(SessionServerSelectors.defaultSubscriptionsEmail) ?? '';

  const memoizedSettings = useMemoizedSettingsWithDefaults(settings, defaultSubscriptionEmail);

  // Mapping to include overrides needed for several settings
  const mappedProps: SiteSettingsForServerAdminProps = {
    settings: memoizedSettings,
    availability: settings.siteSettingsForServerAdmin.availability,
    siteInfo: { // Used by ManageUsers
      ...settings.siteSettingsForServerAdmin
    }
  };

  // the default site will have urlName empty when the component initializes. It shouldn't be recomputed because the user
  // might change it while editing, thus we use it as the initial value of a state.
  const [isDefaultSite] = useState(props.isDefaultSite ?? (mappedProps?.settings.urlName === ''));
  rest = {
    ...rest,
    isDefaultSite
  };

  const submitHandler = useLocalSubmitHandler(settings, defaultSubscriptionEmail);

  return <SettingsFormikContainer
    formName={'ReactSiteSettingsForServerAdmin'}
    {...rest}
    {...mappedProps} // Contains settings and overloads
    sections={serverAdminSections}
    onSubmit={submitHandler}
  />;
};

function useLocalSubmitHandler(settings: ReactSiteSettingsForServerAdminSubFormikProps['settings'], defaultSubscriptionEmail: string) {
  const isInSettingsLocation = useSelector(NavLocationSelectors.inSettingsLocation);
  // since availability isn't being memoized as part of settings, save previous values here.
  const previousAvailability = React.useRef(settings.siteSettingsForServerAdmin.availability);
  return React.useCallback(async (newServerAdminSettings: IServerAdminSettings, availability: string) => {
    const oldSiteSettingsForServerAdmin = settings.siteSettingsForServerAdmin;
    const siteId = settings.siteSettingsForServerAdmin.id;
    const sitesService = Sites.instance;

    let siteAvailabilityResult: IResult;
    if (previousAvailability.current !== availability) {
      siteAvailabilityResult = await sitesService.updateSiteAvailability(
        [siteId], availability
      );

    }

    // Responsibility is to send an update API call and also call refreshSessionInfo if some data changed
    const result: ISiteSettingsForServerAdmin | IResult = await sitesService.updateSiteSettingsForServerAdmin(
      siteId, newServerAdminSettings
    );

    if (result?.errors?.length) {
      throw result.errors.map(e => sitesService.errorMessage(e.code, oldSiteSettingsForServerAdmin));
    } else if (siteAvailabilityResult?.errors?.length) {
      throw siteAvailabilityResult.errors.map(e => sitesService.errorMessage(e.code, oldSiteSettingsForServerAdmin));
    } else {
      if (!isInSettingsLocation) {
        return;
      }

      const newSiteSettingsForSiteAdmin = result as ISiteSettingsForServerAdmin;

      Telemetry.sendEvent<SiteSettingServerAdminTelemetryEvent>(
        buildSiteSettingServerAdminTelemetryEvent(
          newSiteSettingsForSiteAdmin,
          oldSiteSettingsForServerAdmin,
          getSiteSettingUUID(false)
        )
      );

      // Changed site url name in site settings page
      if (newSiteSettingsForSiteAdmin.settings.urlName !== ''
        && newSiteSettingsForSiteAdmin.settings.urlName !== oldSiteSettingsForServerAdmin.settings.urlName) {
        const urls = new SpacesSiteUrls({siteUrlName: newSiteSettingsForSiteAdmin.settings.urlName});
        Location.instance.setUrl(urls.settingsUrl());
        return null;
      }

      // keep our availibility up to date so that if user makes mulitple changes we properly update
      previousAvailability.current = availability;

      return {
        settings: initializeSettingsWithDefaultValues(newSiteSettingsForSiteAdmin.settings, defaultSubscriptionEmail),
        availability: availability
      };
    }

  },
  // This noncompliance existed before the lint rule was enabled.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [isInSettingsLocation, settings?.siteSettingsForServerAdmin]);
}

// The default export is needed for React Dev Tools to see the name of the component. Otherwise it shows up as anonymous
export default ReactSiteSettingsForServerAdmin;
